"use client";

import { startTransition, useActionState, useEffect, useState } from "react";
import { Send } from "lucide-react";
import * as Sentry from "@sentry/nextjs";
import { reportIssueAction, type ReportFormState } from "@/app/[lang]/(app)/profil/prijave/actions";
import { FilePicker } from "@/components/issues/FilePicker";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FieldRow, Textarea } from "@/components/ui/Input";
import { useDict } from "@/components/i18n/DictProvider";
import { DESCRIPTION_MAX } from "@/lib/issues/rules";
import type { Lang } from "@/lib/i18n";

const initial: ReportFormState = { ok: false };

/** What the member has typed and picked so far. */
export type ReportDraft = { description: string; files: File[] };

export const EMPTY_DRAFT: ReportDraft = { description: "", files: [] };

/**
 * One question — what happened — plus the page and optional files: no
 * category to pick and no title to invent (the action takes the first line).
 * A member reports rarely; every decision the form asks for is one more
 * reason not to. Every field is controlled and the action is called by hand: React resets a
 * `<form action>`'s fields after each submission, and a refused send (rate
 * limit, a file the backend sniffs as wrong) must not cost the member a long
 * description or their screenshots.
 *
 * It lives in the "Report a problem" side panel (ReportProblem): the action
 * answers with the new report's id (`onSent`) and the member stays on the page
 * they were reporting. The panel unmounts the form when it closes, so it keeps
 * the draft (`draft` + `onDraft`): Esc or a click outside must not cost a
 * half-written report.
 */
export function ReportForm({
  lang,
  from,
  onSent,
  draft = EMPTY_DRAFT,
  onDraft,
}: {
  lang: Lang;
  from: string | null;
  onSent: (id: string) => void;
  draft?: ReportDraft;
  onDraft?: (draft: ReportDraft) => void;
}) {
  const t = useDict().issues;
  const [state, action, pending] = useActionState(reportIssueAction, initial);
  const [{ description, files }, setDraft] = useState(draft);
  function edit(patch: Partial<ReportDraft>) {
    const next = { description, files, ...patch };
    setDraft(next);
    onDraft?.(next);
  }
  const setDescription = (v: string) => edit({ description: v });
  const setFiles = (f: File[]) => edit({ files: f });
  const [fileError, setFileError] = useState<string | null>(null);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData();
    fd.set("lang", lang);
    fd.set("description", description);
    if (from) fd.set("from", from);
    fd.set("viewport", `${window.innerWidth}x${window.innerHeight}`);
    // The last error Sentry recorded in this tab (an error page, a broken
    // block): the team opens the report and lands on the stack trace. It may
    // predate the problem being reported — a lead, not a verdict. Undefined
    // when nothing failed or Sentry is off (no DSN).
    const eventId = Sentry.lastEventId();
    if (eventId) fd.set("sentryEventId", eventId);
    for (const f of files) fd.append("files", f);
    startTransition(() => action(fd));
  }

  // The panel swaps to its "sent" view on this call — an effect, because it
  // sets the PARENT's state, which React refuses during this render.
  const sentId = state.ok ? state.id : null;
  useEffect(() => {
    if (sentId) onSent(sentId);
  }, [sentId, onSent]);

  return (
    <form onSubmit={submit} className="grid gap-6">
      {!state.ok && state.error ? <Alert tone="error">{state.error}</Alert> : null}

      <FieldRow label={t.descriptionLabel}>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t.descriptionPlaceholder}
          maxLength={DESCRIPTION_MAX}
          rows={6}
          required
        />
      </FieldRow>

      {from ? (
        <div className="flex flex-col gap-1">
          <span className="font-display text-[14px] font-semibold leading-5 text-foreground">
            {t.pageLabel}
          </span>
          <span className="truncate rounded-lg bg-muted/50 px-3 py-2 font-mono text-[12.5px] text-muted-foreground">
            {from}
          </span>
        </div>
      ) : null}

      <FilePicker files={files} onChange={setFiles} error={fileError} onError={setFileError} />

      <div className="flex justify-end pt-2">
        <Button
          type="submit"
          loading={pending}
          loadingLabel={t.sending}
          disabled={!description.trim()}
        >
          <Send className="size-4" aria-hidden />
          {t.submit}
        </Button>
      </div>
    </form>
  );
}
